const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const isAuthenticated = () => {
  return !!localStorage.getItem("auth_token");
};

export const getAuthToken = () => {
  return localStorage.getItem("auth_token");
};

export const clearAuthToken = () => {
  localStorage.removeItem("auth_token");
};

/**
 * Wrapper around fetch that automatically adds auth headers
 * This is the ONLY place we handle auth headers!
 */
const authenticatedFetch = async (url, options = {}) => {
  // Get token from localStorage
  const token = localStorage.getItem("auth_token");

  // Merge headers - auth token is automatically added if it exists
  const headers = {
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  // Make the request with auth headers
  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized globally
  if (response.status === 401) {
    // Token expired or invalid - clear it and redirect to login
    localStorage.removeItem("auth_token");
    window.location.href = "/login";
    throw new Error("Session expired. Please login again.");
  }

  return response;
};

// Custom error class for API errors
export class APIError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

/**
 * Translate any error (APIError, plain Error, network failure) into a
 * user-safe message. UI code should call this rather than reading
 * error.message directly — backend 5xx error strings could leak internals.
 *
 * Rules:
 *   - 4xx (client error) → trust the backend message; it's a curated hint.
 *   - 5xx (server error) → ALWAYS replace with a generic message.
 *   - Network/unknown   → connection-friendly message.
 */
export function getUserMessage(error) {
  if (!error) return "Something went wrong. Please try again.";
  const status = error.status ?? 0;

  if (status === 0)
    return "Can't reach the server. Please check your connection and try again.";
  if (status === 401)
    return error.message || "Your session expired. Please log in again.";
  if (status === 403)
    return error.message || "You don't have permission to do that.";
  if (status === 404) return "We couldn't find what you were looking for.";
  if (status === 408) return "The request timed out. Please try again.";
  if (status === 413)
    return "That file is too large. Please upload a smaller file.";
  if (status === 415) return error.message || "Unsupported file type.";
  if (status === 422)
    return (
      error.message ||
      "We couldn't process that. Please check the input and try again."
    );

  if (status >= 400 && status < 500) {
    // 4xx: backend message is curated and user-facing
    return error.message || "Please check your input and try again.";
  }

  // 5xx or unknown — never expose backend message
  return "Something went wrong on our end. Please try again later.";
}

// Generic error handler for fetch responses
const handleResponse = async (response) => {
  try {
    const contentType = response.headers.get("content-type");

    if (!response.ok) {
      let errorData = null;
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        if (contentType && contentType.includes("application/json")) {
          errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textData = await response.text();
          if (textData) {
            errorMessage = textData;
          }
        }
      } catch (parseError) {
        console.warn("Failed to parse error response:", parseError);
      }

      throw new APIError(errorMessage, response.status, errorData);
    }

    // Handle successful responses
    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    } else {
      return await response.text();
    }
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(`Response parsing failed: ${error.message}`, 500);
  }
};

// Generic request wrapper with timeout and retry logic
const makeRequest = async (
  url,
  options = {},
  retries = 0,
  timeout = 30000000,
) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const requestOptions = {
    ...options,
    signal: controller.signal,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, requestOptions);
    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle specific error types
    if (error.name === "AbortError") {
      throw new APIError("Request timeout - please try again", 408);
    }

    if (error instanceof APIError) {
      throw error;
    }

    // Network errors - retry logic
    if (
      retries > 0 &&
      (error.name === "TypeError" || error.message.includes("fetch"))
    ) {
      console.warn(`Request failed, retrying... (${retries} attempts left)`);
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1s before retry
      return makeRequest(url, options, retries - 1, timeout);
    }

    throw new APIError(`Network error: ${error.message}`, 0);
  }
};

/*
 * Register new user with email and password
 */
export const registerUser = async (email, password, firstName, lastName) => {
  try {
    const response = await fetch(`${API_BASE}/api/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        password,
        firstName,
        lastName,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.error || "Registration failed",
        response.status,
        data,
      );
    }

    // Store token
    if (data.token) {
      localStorage.setItem("auth_token", data.token);
    }

    return {
      success: true,
      token: data.token,
      message: data.message,
    };
  } catch (error) {
    console.error("Registration error");
    return {
      success: false,
      error: getUserMessage(error),
      status: error.status ?? 0,
    };
  }
};

/*
 * Send verification email for new user registration
 */
export const sendVerificationEmail = async (email) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/auth/send-verification-email`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
        }),
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.error || "Verification email sending failed",
        response.status,
        data,
      );
    }

    return {
      success: true,
      message: data.message,
    };
  } catch (error) {
    console.error("Verification email sending error");
    return {
      success: false,
      error: getUserMessage(error),
      status: error.status ?? 0,
    };
  }
};

/*
 * Verifying code for new user registration
 */
export const verifyCode = async (email, code) => {
  try {
    const response = await fetch(`${API_BASE}/api/auth/verify-code`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        code,
      }),
    });

    const data = await response.json();

    if ("error" in data) {
      throw new APIError(
        data.error || "Email verification failed",
        response.status,
        data,
      );
    }

    return data;
  } catch (error) {
    console.error("Verification error");
    return {
      success: false,
      verified: false,
      error: getUserMessage(error),
      status: error.status ?? 0,
    };
  }
};

/**
 * Login with email and password
 */
export const loginUser = async (email, password) => {
  try {
    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(data.error || "Login failed", response.status, data);
    }

    // Store token
    if (data.token) {
      localStorage.setItem("auth_token", data.token);
    }

    return {
      success: true,
      token: data.token,
    };
  } catch (error) {
    console.error("Login error");
    return {
      success: false,
      error: getUserMessage(error),
      status: error.status ?? 0,
    };
  }
};

/**
 * Verify current authentication token
 */
export const verifyToken = async () => {
  try {
    const response = await fetch(`${API_BASE}/api/auth/verify`, {
      method: "GET",
      headers: {
        ...getAuthHeaders(),
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.error || "Token verification failed",
        response.status,
        data,
      );
    }

    return {
      success: true,
      valid: data.valid,
      user: data.user,
    };
  } catch (error) {
    console.error("Token verification error");
    return {
      success: false,
      valid: false,
      error: getUserMessage(error),
      status: error.status ?? 0,
    };
  }
};

/**
 * Logout user
 */
export const logoutUser = async () => {
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: "POST",
      headers: {
        ...getAuthHeaders(),
      },
    });

    // Remove token regardless of response
    localStorage.removeItem("auth_token");

    return { success: true };
  } catch (error) {
    console.error("Logout error");
    // Still remove token even if request fails
    localStorage.removeItem("auth_token");
    return { success: true };
  }
};

/**
 * Get current authenticated user data
 */
export const getCurrentUser = async () => {
  try {
    const response = await fetch(`${API_BASE}/api/auth/me`, {
      method: "GET",
      headers: {
        ...getAuthHeaders(),
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.error || "Failed to get user data",
        response.status,
        data,
      );
    }

    return {
      success: true,
      user: data,
    };
  } catch (error) {
    console.error("Get current user error");
    return {
      success: false,
      error: getUserMessage(error),
      status: error.status ?? 0,
    };
  }
};

/*
 * Sending forgot password email
 */
export const forgotPasswordEmail = async (email) => {
  try {
    const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.error || "Sending email verification failed",
        response.status,
        data,
      );
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: getUserMessage(error),
      status: error.status ?? 0,
    };
  }
};

/*
 * Resetting password
 */
export const resetPassword = async (email, token, password) => {
  try {
    const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        token,
        password,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(
        data.error || "Resetting password failed",
        response.status,
        data,
      );
    }

    return data;
  } catch (error) {
    console.error("Password reset error");
    return {
      success: false,
      error: getUserMessage(error),
      status: error.status ?? 0,
    };
  }
};

export const saveProfile = async (email, profileData) => {
  const response = await authenticatedFetch(`${API_BASE}/api/users/save`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: email,
      profile: profileData,
    }),
  });

  if (!response.ok) {
    throw new APIError(`HTTP ${response.status}`, response.status);
  }

  return response.json();
};

export const saveResumeData = async (email, resumeData, oldResumeName = "") => {
  const resumeName = resumeData.metadata.name;
  const resume = {
    [resumeName]: resumeData,
  };
  const response = await authenticatedFetch(`${API_BASE}/api/users/save`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: email,
      resume: resume,
      old_resume_name: oldResumeName,
    }),
  });

  if (!response.ok) {
    throw new APIError(`HTTP ${response.status}`, response.status);
  }

  return response.json();
};

export const deleteResume = async (email, selectedResume) => {
  const response = await authenticatedFetch(`${API_BASE}/api/resumes/delete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: email,
      resume: selectedResume,
    }),
  });

  if (!response.ok) {
    throw new APIError(`HTTP ${response.status}`, response.status);
  }

  return response.json();
};

export const downloadResumePdf = async (email, selectedResume) => {
  try {
    if (!email || typeof email !== "string" || !email.includes("@")) {
      throw new APIError("Valid email address is required", 400);
    }

    if (!selectedResume) {
      throw new APIError("Please select a resume to download.", 400);
    }

    const response = await makeRequest(`${API_BASE}/api/resumes/download-pdf`, {
      method: "POST",
      body: JSON.stringify({
        email,
        selected_resume: selectedResume,
      }),
    });

    if (response.success) {
      return response;
    }

    throw new APIError("Failed to download resume", 500);
  } catch (error) {
    console.error("Download resume PDF error");
    throw error;
  }
};

export const saveJobPreferences = async (email, jobPreferences) => {
  const response = await authenticatedFetch(`${API_BASE}/api/users/save`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: email,
      job_preferences: jobPreferences,
    }),
  });

  if (!response.ok) {
    throw new APIError(`HTTP ${response.status}`, response.status);
  }

  return response.json();
};

export const saveSubscription = async (email, subscriptionData) => {
  const response = await authenticatedFetch(`${API_BASE}/api/users/save`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: email,
      subscription: subscriptionData,
    }),
  });

  if (!response.ok) {
    throw new APIError(`HTTP ${response.status}`, response.status);
  }

  return response.json();
};

export const cancelSubscription = async (email) => {
  const response = await authenticatedFetch(
    `${API_BASE}/api/users/cancel-subscription`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
      }),
    },
  );

  if (!(response.status === 200)) {
    throw new APIError(`HTTP ${response.status}`, response.status);
  }

  return response.json();
};

export const createCheckoutSession = async (email, tier) => {
  try {
    const response = await authenticatedFetch(
      `${API_BASE}/api/payments/create-checkout-session`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, tier }),
      },
    );

    if (response.ok) {
      return response.json();
    } else {
      throw new APIError("Failed to start payment", 500);
    }
  } catch (error) {
    throw error;
  }
};

export const checkResumeExists = async (email) => {
  try {
    if (!email || typeof email !== "string" || !email.includes("@")) {
      throw new APIError("Valid email address is required", 400);
    }

    const response = await makeRequest(
      `${API_BASE}/api/resumes/check-resume-exists`,
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
    );

    return response;
  } catch (error) {
    console.error("Check resume exists error");
    throw error;
  }
};

export const parseResume = async (
  file,
  email,
  { onProgress, onComplete, onError } = {},
) => {
  try {
    if (!file || !(file instanceof File)) {
      throw new APIError("Valid file is required", 400);
    }

    if (!email || typeof email !== "string" || !email.includes("@")) {
      throw new APIError("Valid email address is required", 400);
    }

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new APIError("Only PDF, DOC, and DOCX files are supported", 400);
    }

    const maxSize = 16 * 1024 * 1024;

    if (file.size > maxSize) {
      throw new APIError("File size must be less than 16MB", 413);
    }

    const formData = new FormData();

    formData.append("resume", file);
    formData.append("user_email", email);

    const response = await authenticatedFetch(`${API_BASE}/api/resumes/parse`, {
      method: "POST",
      body: formData,
      headers: {},
    });

    if (!response.ok) {
      const errorText = await response.text();

      throw new APIError(
        errorText || `HTTP ${response.status}`,
        response.status,
      );
    }

    const reader = response.body.getReader();

    const decoder = new TextDecoder();

    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, {
        stream: true,
      });

      const events = buffer.split("\n\n");

      buffer = events.pop();

      for (const event of events) {
        if (!event.startsWith("data:")) {
          continue;
        }

        const jsonString = event.replace("data:", "").trim();

        if (!jsonString) continue;

        const data = JSON.parse(jsonString);

        // PROGRESS EVENTS
        if (data.type === "progress") {
          if (onProgress) {
            onProgress(data);
          }
        }

        // COMPLETE EVENT
        else if (data.type === "complete") {
          if (onComplete) {
            onComplete(data.data);
          }

          return data.data;
        }

        // ERROR EVENT
        else if (data.type === "error") {
          if (onError) {
            onError(data.message);
          }

          throw new APIError(data.message, 500);
        }
      }
    }
  } catch (error) {
    if (error.status === 413) {
      throw new APIError(
        "File is too large. Please upload a file smaller than 16MB.",
        413,
      );
    } else if (error.status === 415) {
      throw new APIError(
        "Unsupported file type. Please upload a text-based PDF, DOC, or DOCX file.",
        415,
      );
    } else if (error.status === 500) {
      throw new APIError("Resume parsing failed. Please try again.", 500);
    }

    throw error;
  }
};

export const generateTailoredResume = async (
  email,
  jobDescription,
  jobTitle,
  selectedResume,
  baselineAtsScore,
) => {
  try {
    if (!jobDescription || typeof jobDescription !== "string") {
      console.log(
        "Resume will simply be refined if there is no job description.",
      );
    }

    const response = await makeRequest(`${API_BASE}/api/resumes/tailor`, {
      method: "POST",
      body: JSON.stringify({
        user_email: email,
        job_description: jobDescription,
        job_title: jobTitle,
        selected_resume: selectedResume,
        baseline_ats_score: baselineAtsScore,
      }),
    });

    if (response.success) {
      return response;
    } else {
      throw new APIError("Failed to generate tailored resume", 500);
    }
  } catch (error) {
    console.error("Generate tailored resume error");
    throw error;
  }
};

export const submitTailoringRating = async ({
  email,
  rating,
  selectedResume,
  jobDescription,
  jobTitle,
  atsScore,
}) => {
  try {
    const response = await makeRequest(
      `${API_BASE}/api/resumes/tailoring-rating`,
      {
        method: "POST",
        body: JSON.stringify({
          email,
          rating,
          selected_resume: selectedResume,
          job_description: jobDescription,
          job_title: jobTitle,
          ats_score: atsScore,
        }),
      },
    );

    if (response.success) {
      return response;
    }
    throw new APIError("Failed to save tailoring rating", 500);
  } catch (error) {
    console.error("Submit tailoring rating error");
    throw error;
  }
};

export const generateCoverLetter = async (
  userEmail,
  resume,
  jobDescription,
  jobTitle,
  company,
) => {
  try {
    const response = await makeRequest(
      `${API_BASE}/api/resumes/generate-cover-letter`,
      {
        method: "POST",
        body: JSON.stringify({
          email: userEmail,
          resume: resume,
          job_description: jobDescription,
          job_title: jobTitle,
          company: company,
        }),
      },
    );

    if (response.success) {
      return response.cover_letter_base64;
    } else {
      throw response.error;
    }
  } catch (error) {
    console.error("Generate tailored resume error");
    throw error;
  }
};

export const getJobDescriptionDetails = async (url) => {
  try {
    const llm_details = await makeRequest(
      `${API_BASE}/api/jobs/description-with-details`,
      {
        method: "POST",
        body: JSON.stringify({ url }),
      },
    );

    if (!llm_details.ok) {
      throw llm_details.error;
    }
    return llm_details.details;
  } catch (error) {
    if (error.status === 408) {
      throw new APIError(
        "Job validation timed out. The website may be slow to respond.",
        408,
      );
    }
  }
};

export const validateJob = async (url) => {
  try {
    if (!url || typeof url !== "string") {
      throw new APIError("Valid URL is required", 400);
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      throw new APIError("Invalid URL format", 400);
    }

    const response = await makeRequest(`${API_BASE}/api/jobs/validate`, {
      method: "POST",
      body: JSON.stringify({ url }),
    });
    if (response.status === 500) {
      throw new APIError("Validation Error", 400);
    }

    const llm_details = await makeRequest(
      `${API_BASE}/api/jobs/description-with-details`,
      {
        method: "POST",
        body: JSON.stringify({ url }),
      },
    );

    if (llm_details.status === 500) {
      throw new APIError("Error extracting job details with llm", 400);
    }

    return {
      ...response,
      details: { ...response.details, ...llm_details.details },
    };
  } catch (error) {
    if (error.status === 408) {
      throw new APIError(
        "Job validation timed out. The website may be slow to respond.",
        408,
      );
    }

    throw error;
  }
};

export const getJobDetails = async (url) => {
  try {
    if (!url || typeof url !== "string") {
      throw new APIError("Valid URL is required", 400);
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      throw new APIError("Invalid URL format", 400);
    }

    const response = await makeRequest(`${API_BASE}/api/jobs/details`, {
      method: "POST",
      body: JSON.stringify({ url }),
    }); // 15s timeout for job validation

    return response;
  } catch (error) {
    console.error("Job details retrieval error");

    if (error.status === 408) {
      throw new APIError(
        "Job details retrieval timed out. The website may be slow to respond.",
        408,
      );
    }

    throw error;
  }
};

export const getJobDetailsFromText = async (jobDescription) => {
  try {
    if (!jobDescription || typeof jobDescription !== "string") {
      throw new APIError("Valid job description is required", 400);
    }

    const response = await makeRequest(
      `${API_BASE}/api/jobs/details-from-text`,
      {
        method: "POST",
        body: JSON.stringify({ job_description: jobDescription }),
      },
    ); // 15s timeout for job validation
    return response;
  } catch (error) {
    console.error("Job details retrieval error");

    if (error.status === 408) {
      throw new APIError(
        "Job details retrieval timed out. The website may be slow to respond.",
        408,
      );
    }

    throw error;
  }
};

export const saveJob = async (jobData, userEmail) => {
  try {
    if (!jobData || !userEmail) {
      throw new APIError("Job data and user email are required", 400);
    }

    const response = await makeRequest(`${API_BASE}/api/jobs/save`, {
      method: "POST",
      body: JSON.stringify({
        jobData,
        userEmail,
      }),
    });

    if ("error" in response) {
      throw new APIError("Error saving job", response.status);
    }

    return response;
  } catch (error) {
    console.error("Save job error");
    throw error;
  }
};

export const getSavedJobs = async (userEmail) => {
  try {
    if (!userEmail) {
      throw new APIError("User email is required", 400);
    }

    const response = await makeRequest(
      `${API_BASE}/api/jobs/saved?email=${encodeURIComponent(userEmail)}`,
      {
        method: "GET",
      },
    );

    return response;
  } catch (error) {
    console.error("Get saved jobs error");
    throw error;
  }
};

export const getMatchScores = async (userEmail, description) => {
  try {
    if (!userEmail) {
      throw new APIError("User email is required", 400);
    }

    const response = await makeRequest(
      `${API_BASE}/api/jobs/get-match-scores`,
      {
        method: "POST",
        body: JSON.stringify({
          userEmail,
          description,
        }),
      },
    );

    if (!response.success) {
      throw new APIError(`HTTP ${response.status}`, response.status);
    }

    return response.matchScores;
  } catch (error) {
    console.error("Get match scores error");
    throw error;
  }
};

export const refreshMatchScoresForAll = async (userEmail) => {
  try {
    if (!userEmail) {
      throw new APIError("User email is required", 400);
    }

    const response = await makeRequest(`${API_BASE}/api/jobs/refresh-scores`, {
      method: "POST",
      body: JSON.stringify({
        userEmail,
      }),
    });

    if (!response.success) {
      throw new APIError(`HTTP ${response.status}`, response.status);
    }

    return response.saved_jobs;
  } catch (error) {
    console.error("Get saved jobs error");
    throw error;
  }
};

export const deleteJob = async (jobId, userEmail) => {
  try {
    const response = await makeRequest(`${API_BASE}/api/jobs/delete`, {
      method: "DELETE",
      body: JSON.stringify({
        job_id: jobId,
        email: userEmail,
      }),
    });

    if (!response.success) {
      throw new APIError(`HTTP ${response.status}`, response.status);
    }

    return response;
  } catch (error) {
    console.error("Delete job error");
    throw error;
  }
};

export const saveJobApplication = async (email, applicationDetails) => {
  try {
    const response = await authenticatedFetch(
      `${API_BASE}/api/applications/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          application_details: applicationDetails,
        }),
      },
    );

    if (!response.ok) {
      throw new APIError(`HTTP ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    console.error("Save application error");
    throw error;
  }
};

export const deleteJobApplication = async (appId, userEmail) => {
  try {
    if (!appId || !userEmail) {
      throw new APIError("App ID and user email are required", 400);
    }

    const response = await makeRequest(`${API_BASE}/api/applications/delete`, {
      method: "DELETE",
      body: JSON.stringify({
        app_id: appId,
        email: userEmail,
      }),
    });

    return response;
  } catch (error) {
    console.error("Delete job error");
    throw error;
  }
};

export const applyToJob = async (jobUrl, userEmail) => {
  try {
    if (!jobUrl || typeof jobUrl !== "string") {
      throw new APIError("Valid job URL is required", 400);
    }

    if (
      !userEmail ||
      typeof userEmail !== "string" ||
      !userEmail.includes("@")
    ) {
      throw new APIError("Valid email address is required", 400);
    }

    const requestBody = {
      job_url: jobUrl,
      user_email: userEmail,
    };

    const response = await authenticatedFetch(
      `${API_BASE}/api/applications/apply`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      },
    );

    const result = await response.json();

    // NEW: Handle missing fields response (HTTP 202)
    if (response.status === 202 && result.requires_user_input) {
      return {
        success: false,
        requires_user_input: true,
        missing_fields_data: {
          title: "Complete Required Information",
          message: result.user_prompt_data.message,
          job_url: result.job_url,
          fields: transformMissingFieldsForPopup(
            result.user_prompt_data.fields || [],
          ),
        },
        original_response: result,
      };
    }

    // Handle other responses normally
    if (!response.ok) {
      throw new APIError(result.error || "Application failed", response.status);
    }

    return result;
  } catch (error) {
    console.error("Job application error");

    if (error.status === 408) {
      throw new APIError(
        "Job application timed out. This may happen with complex application forms.",
        408,
      );
    } else if (error.status === 429) {
      throw new APIError(
        "Too many applications. Please wait before applying to more jobs.",
        429,
      );
    }

    throw error;
  }
};

export const searchJobs = async (query = "", filters = {}) => {
  try {
    const searchParams = new URLSearchParams();

    if (query && typeof query === "string") {
      searchParams.append("q", query.trim());
    }

    // Add filters if provided
    if (filters.location) {
      searchParams.append("location", filters.location);
    }

    if (filters.jobType) {
      searchParams.append("job_type", filters.jobType);
    }

    if (filters.salary_min) {
      searchParams.append("salary_min", filters.salary_min);
    }

    if (filters.salary_max) {
      searchParams.append("salary_max", filters.salary_max);
    }

    const url = `${API_BASE}/api/jobs/search${searchParams.toString() ? "?" + searchParams.toString() : ""}`;
    const response = await makeRequest(url, { method: "GET" });

    return response;
  } catch (error) {
    console.error("Job search error");
    throw error;
  }
};

export const getAllApplications = async (email) => {
  try {
    if (!email || typeof email !== "string" || !email.includes("@")) {
      throw new APIError("Valid email address is required", 400);
    }

    const response = await makeRequest(
      `${API_BASE}/api/applications/history?email=${encodeURIComponent(email)}`,
      {
        method: "GET",
      },
    );

    return response;
  } catch (error) {
    console.error("Get application history error");
    throw error;
  }
};

export const getApplicationInsights = async (user) => {
  try {
    if (!user || typeof user !== "string" || !user.includes("@")) {
      throw new APIError("Valid email address is required", 400);
    }
    return await makeRequest(
      `${API_BASE}/api/applications/insights?email=${encodeURIComponent(user)}`,
      {
        method: "GET",
      },
    );
  } catch (error) {
    console.error("Get application insights error");
    throw error;
  }
};

export const reanalyzeApplicationInsights = async (force = false, user) => {
  try {
    return await makeRequest(
      `${API_BASE}/api/applications/insights/reanalyze`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ force, email: user }),
      },
    );
  } catch (error) {
    console.error("Reanalyze application insights error");
    throw error;
  }
};

export const recalculateApplicationInsightStats = async (
  force = false,
  user,
) => {
  try {
    return await makeRequest(
      `${API_BASE}/api/applications/insights/recalculate-stats`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ force, email: user }),
      },
    );
  } catch (error) {
    console.error("Recalculate application insight stats error");
    throw error;
  }
};

export const reanalyzeApplicationInsightPatterns = async (
  force = false,
  user,
) => {
  try {
    return await makeRequest(
      `${API_BASE}/api/applications/insights/reanalyze-patterns`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ force, email: user }),
      },
    );
  } catch (error) {
    console.error("Reanalyze application insight patterns error");
    throw error;
  }
};

export const getUserProfile = async (
  email,
  profileData = false,
  resumeData = false,
  jobPreferences = false,
  subscription = false,
) => {
  try {
    if (!email || typeof email !== "string" || !email.includes("@")) {
      throw new APIError("Valid email address is required", 400);
    }

    const response = await authenticatedFetch(
      `${API_BASE}/api/users/user-profile`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          profile_data: profileData,
          resume_data: resumeData,
          job_preferences: jobPreferences,
          subscription: subscription,
        }),
      },
    );

    return response.json();
  } catch (error) {
    if (error.status === 404) {
      return null; // No profile found
    }
  }
};

// Health check endpoint
export const checkAPIHealth = async () => {
  try {
    const response = await makeRequest(
      `${API_BASE}/api/health`,
      {
        method: "GET",
      },
      0,
      5000,
    ); // 5s timeout, no retries

    return response;
  } catch (error) {
    console.error("API health check error");
    return { status: "unhealthy", error: error.message };
  }
};

export const completeApplicationWithAdditionalFields = async (
  jobUrl,
  userEmail,
  additionalFields,
) => {
  try {
    if (!jobUrl || !userEmail || !additionalFields) {
      throw new APIError(
        "Job URL, user email, and additional fields are required",
        400,
      );
    }

    const requestBody = {
      job_url: jobUrl,
      user_email: userEmail,
      additional_fields: additionalFields,
    };

    const response = await authenticatedFetch(
      `${API_BASE}/api/applications/complete-application`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      },
    );

    const result = await response.json();

    if (!response.ok) {
      throw new APIError(
        result.error || "Failed to complete application",
        response.status,
      );
    }

    return result;
  } catch (error) {
    console.error("Complete application error");
    throw error;
  }
};

const transformMissingFieldsForPopup = (missingFields) => {
  return missingFields.map((field) => {
    const fieldConfig = {
      type: "text",
      placeholder: "Please provide this information",
      options: ["yes", "no"],
    };

    return {
      id: field.id,
      name: field.name,
      label: field.label || generateFriendlyLabel(field.id),
      type: field.type || fieldConfig.type,
      placeholder: field.placeholder || fieldConfig.placeholder,
      options: field.options || fieldConfig.options,
      required: true,
    };
  });
};

export const submitContactForm = async (contactFormInfo) => {
  try {
    if (
      !contactFormInfo.name ||
      !contactFormInfo.email ||
      !contactFormInfo.subject ||
      !contactFormInfo.message
    ) {
      throw new APIError("All fields are required", 400);
    }

    const response = await authenticatedFetch(`${API_BASE}/api/users/contact`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(contactFormInfo),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new APIError(result.error || "Failed to submit", response.status);
    }

    return result;
  } catch (error) {
    console.error("Submit contact form error");
    throw error;
  }
};

export const deleteAllData = async (email) => {
  try {
    const response = await authenticatedFetch(`${API_BASE}/api/users/delete`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
      }),
    });

    if (!response.ok) {
      throw new APIError(`HTTP ${response.status}`, response.status);
    }

    return response.json();
  } catch (error) {
    throw error;
  }
};

export const sendErrorReport = async (
  userEmail,
  subject,
  errorMsg,
  consent,
) => {
  try {
    const response = await authenticatedFetch(
      `${API_BASE}/api/users/report-errors`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userEmail,
          subject: subject,
          message: errorMsg,
          consent: consent,
        }),
      },
    );

    if ("error" in response) {
      console.error(response.error);
    }
  } catch (error) {
    console.error("Send error report error");
  }
};

export const addToReferralWaitlist = async (email) => {
  try {
    const response = await authenticatedFetch(
      `${API_BASE}/api/users/referral-waitlist-join`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
        }),
      },
    );

    if ("error" in response) {
      console.error(response.error);
    }
    return response.json();
  } catch (error) {
    console.error("Add to referral waitlist error");
  }
};

const generateFriendlyLabel = (fieldId) => {
  const labelMap = {
    school: "School",
    degree: "Degree",
    end_date_month: "End Date Month",
    end_date_year: "End Date Year",
    fall_availability: "Fall Internship Availability",
    full_time_available: "Available Full-Time (40 hours/week)?",
    onsite_available: "Available for Onsite Work?",
    favorite_cpp_feature: "Favorite C++ Feature",
    work_visa: "Work Visa Required?",
    how_heard: "How did you hear about us?",
  };

  return (
    labelMap[fieldId] ||
    fieldId.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

export const getSkillGapData = async (email) => {
  try {
    if (!email || typeof email !== "string" || !email.includes("@")) {
      throw new APIError("Valid email address is required", 400);
    }
    const response = await makeRequest(
      `${API_BASE}/api/users/skill-gap?email=${encodeURIComponent(email)}`,
      { method: "GET" },
    );
    return response;
  } catch (error) {
    console.error("Skill gap fetch error");
    throw error;
  }
};

export const swapSkillGapTitle = async (email, url) => {
  try {
    if (!email || typeof email !== "string" || !email.includes("@")) {
      throw new APIError("Valid email address is required", 400);
    }
    if (!url || typeof url !== "string" || !url.startsWith("http")) {
      throw new APIError("A valid job posting URL is required", 400);
    }
    const response = await makeRequest(
      `${API_BASE}/api/users/skill-gap/swap-title`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, url }),
      },
    );
    return response;
  } catch (error) {
    console.error("Swap title error");
    throw error;
  }
};

export const sendAssistantChat = async (
  message,
  currentScreen = "",
  conversation = [],
) => {
  const response = await authenticatedFetch(`${API_BASE}/api/assistant/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ message, currentScreen, conversation }),
  });
  return response.json();
};

// Export the custom error class for use in components
// TODO: apply bulk, individual health checks
